
import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Calendar, User, Eye, Tag } from "lucide-react";
import { Article, ArticleStatus } from "@/types/article-types";
import { supabase } from "@/integrations/supabase/client";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import MetaTags from "@/components/meta/MetaTags";
import { format } from "date-fns";
import DOMPurify from "isomorphic-dompurify";
import { marked } from "marked";

const ArticlePage = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [article, setArticle] = useState<Article | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [relatedArticles, setRelatedArticles] = useState<Article[]>([]);

  useEffect(() => {
    const fetchArticle = async () => {
      if (!slug) return;
      
      try {
        setLoading(true);
        
        const { data, error } = await supabase
          .from('articles')
          .select(`
            *,
            author:author_id (
              id,
              first_name,
              last_name,
              profile_image_url,
              bio
            ),
            tags:article_tags_map (
              tag:tag_id (
                id,
                name,
                slug
              )
            )
          `)
          .eq('slug', slug)
          .eq('status', 'published')
          .single();

        if (error) throw error;
        
        if (!data) {
          navigate('/not-found', { replace: true });
          return;
        }
        
        // Transform tags structure to match our Article type
        const transformedArticle: Article = {
          ...data,
          status: data.status as ArticleStatus,
          tags: (data.tags || [])
            .filter((tagItem: any) => tagItem && tagItem.tag)
            .map((tagItem: any) => tagItem.tag)
        };
        
        setArticle(transformedArticle);
        
        // Increment view count
        await supabase
          .from('articles')
          .update({ views: (data.views || 0) + 1 })
          .eq('id', data.id);
          
        // Fetch related articles based on tags
        if (transformedArticle.tags && transformedArticle.tags.length > 0) {
          const tagIds = transformedArticle.tags.map(tag => tag.id);
          
          const { data: relatedData } = await supabase
            .from('article_tags_map')
            .select(`
              article:article_id (
                id,
                title,
                slug,
                lead_text,
                header_image_url,
                publish_date,
                created_at,
                content,
                status,
                author:author_id (
                  first_name,
                  last_name
                )
              )
            `)
            .in('tag_id', tagIds)
            .neq('article_id', transformedArticle.id)
            .order('created_at', { ascending: false })
            .limit(3);
            
          if (relatedData) {
            // Extract unique articles and ensure they match the Article type
            const uniqueArticleMap = new Map();
            relatedData.forEach(item => {
              if (item.article && !uniqueArticleMap.has(item.article.id)) {
                uniqueArticleMap.set(item.article.id, {
                  ...item.article,
                  status: item.article.status as ArticleStatus
                });
              }
            });
            
            const uniqueArticles = Array.from(uniqueArticleMap.values());
            setRelatedArticles(uniqueArticles as Article[]);
          }
        }
        
      } catch (err: any) {
        console.error("Error fetching article:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchArticle();
  }, [slug, navigate]);

  // Function to render markdown content
  const renderContent = (content: string) => {
    if (!content) return "";
    
    // Configure marked options
    marked.setOptions({
      gfm: true,
      breaks: true
    });
    
    // Parse markdown to HTML and sanitize
    const parsedMarkdown = marked.parse(content) as string;
    const sanitizedHtml = DOMPurify.sanitize(parsedMarkdown);
    
    return sanitizedHtml;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <div className="flex-grow flex items-center justify-center">
          <div className="animate-pulse text-xl">Loading article...</div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <div className="flex-grow flex items-center justify-center">
          <div className="text-red-500 text-center px-4">
            <h2 className="text-2xl font-bold mb-2">Error Loading Article</h2>
            <p>{error || "Article not found"}</p>
            <Link to="/knowledge-hub" className="text-blue-500 hover:underline mt-4 inline-block">
              Return to Knowledge Hub
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <MetaTags
        customTitle={`${article?.title || 'Article'} | METRADE`}
        customDescription={article?.lead_text || ""}
        customImage={article?.header_image_url || ""}
        customUrl={`https://metrade.io/articles/${slug}`}
      />
      <Header />
      <main className="flex-grow">
        {/* Article Header */}
        <div className="w-full bg-gray-100">
          <div className="container mx-auto px-4 py-8">
            <div className="max-w-3xl mx-auto">
              <Link 
                to="/knowledge-hub/blog" 
                className="inline-flex items-center text-corporate-primary-75 hover:text-corporate-secondary-100 mb-4"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Blog
              </Link>
              <h1 className="text-3xl md:text-5xl font-bold text-corporate-primary-100 mb-4">
                {article?.title}
              </h1>
              {article?.lead_text && (
                <p className="text-lg text-gray-600 mb-6">
                  {article.lead_text}
                </p>
              )}
              <div className="flex flex-wrap items-center text-sm text-gray-500 gap-4 mb-6">
                <div className="flex items-center">
                  <Calendar className="mr-1 h-4 w-4" />
                  {article?.publish_date ? (
                    format(new Date(article.publish_date), 'MMM d, yyyy')
                  ) : article?.created_at ? (
                    format(new Date(article.created_at), 'MMM d, yyyy')
                  ) : ''}
                </div>
                {article?.author && (
                  <div className="flex items-center">
                    <User className="mr-1 h-4 w-4" />
                    {`${article.author.first_name} ${article.author.last_name}`}
                  </div>
                )}
                <div className="flex items-center">
                  <Eye className="mr-1 h-4 w-4" />
                  {article?.views || 0} views
                </div>
              </div>
              {article?.tags && article.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-6">
                  {article.tags.map(tag => (
                    <span 
                      key={tag.id}
                      className="inline-flex items-center px-3 py-1 rounded-full text-xs bg-gray-200 text-gray-700"
                    >
                      <Tag className="mr-1 h-3 w-3" />
                      {tag.name}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Article Featured Image */}
        {article?.header_image_url && (
          <div className="w-full py-4">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <div className="aspect-w-16 aspect-h-9 rounded-xl overflow-hidden">
                  <img 
                    src={article.header_image_url} 
                    alt={article.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Article Content */}
        {article && (
          <div className="container mx-auto px-4 py-8">
            <div className="max-w-3xl mx-auto">
              <article className="prose prose-lg max-w-none">
                <div dangerouslySetInnerHTML={{ __html: renderContent(article.content) }} />
              </article>
              
              {/* Author Bio */}
              {article.author && article.author.bio && (
                <div className="mt-12 pt-6 border-t border-gray-200">
                  <div className="flex items-start gap-4">
                    {article.author.profile_image_url && (
                      <img 
                        src={article.author.profile_image_url}
                        alt={`${article.author.first_name} ${article.author.last_name}`}
                        className="w-16 h-16 rounded-full object-cover"
                      />
                    )}
                    <div>
                      <h3 className="font-bold text-lg mb-1">
                        {`${article.author.first_name} ${article.author.last_name}`}
                      </h3>
                      <p className="text-gray-600 text-sm">
                        {article.author.bio}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <div className="bg-gray-50 py-12">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-2xl font-bold mb-6 text-center text-corporate-primary-75">
                  Related Articles
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {relatedArticles.map((relatedArticle) => (
                    <Link 
                      key={relatedArticle.id}
                      to={`/articles/${relatedArticle.slug}`}
                      className="bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                    >
                      <div className="h-40 overflow-hidden bg-gray-100">
                        <img
                          src={relatedArticle.header_image_url || "/lovable-uploads/bf9cdc94-db08-4840-bae6-869c2f0c41bb.png"}
                          alt={relatedArticle.title}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="p-4">
                        <h3 className="font-bold mb-2 text-corporate-primary-100 hover:text-corporate-secondary-100 transition-colors">
                          {relatedArticle.title}
                        </h3>
                        <div className="flex justify-between items-center text-xs text-gray-500">
                          <span>
                            {relatedArticle.author ? 
                              `${relatedArticle.author.first_name} ${relatedArticle.author.last_name}` : 
                              "METRADE Team"
                            }
                          </span>
                          <span>
                            {relatedArticle.publish_date ? 
                              format(new Date(relatedArticle.publish_date), 'MMM d, yyyy') : 
                              format(new Date(relatedArticle.created_at), 'MMM d, yyyy')
                            }
                          </span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default ArticlePage;
